import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { Skeleton } from "@trillion/ui/components/ui/skeleton";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import { IntentForm } from "@/components/intents/intent-form";
import type { IntentFormData } from "@/components/intents/intent-form";
import type { TrafficIntent } from "@/types";

export function IntentEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const numericId = Number(id);

  const [intent, setIntent] = useState<TrafficIntent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadIntent() {
      try {
        const data = await adminApi.intents.get(numericId);
        setIntent(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load intent");
        toast.error("Failed to load intent");
      } finally {
        setIsLoading(false);
      }
    }
    if (numericId) loadIntent();
  }, [numericId]);

  async function handleUpdate(data: IntentFormData) {
    await adminApi.intents.update(numericId, {
      topic: data.topic,
      category: data.category || undefined,
      buying_stage: data.buyingStage,
      channel: data.channel || undefined,
      confidence: data.confidence,
      greeting: data.greeting || undefined,
      suggestion_chips: data.suggestionChips.length ? data.suggestionChips : null,
      geo_relevance: data.geoRelevance || undefined,
    });
    toast.success("Intent updated successfully");
    navigate("/intents");
  }

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Edit Intent"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Intents", href: "/intents" },
          { label: `Edit #${numericId}` },
        ]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Edit Intent"
          description={
            intent
              ? `Editing intent for ${intent.source_type}: ${intent.source_key}`
              : "Loading..."
          }
        />
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        ) : error ? (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : intent ? (
          <IntentForm
            intent={intent}
            onSubmit={handleUpdate}
            submitLabel="Update Intent"
          />
        ) : null}
      </div>
    </div>
  );
}
